import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Alert, Card, Spinner, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import html2pdf from 'html2pdf.js';
import exitStrategyService from '../services/exitStrategyService';
import authService from '../services/authService';
import businessService from '../services/businessService';
import BackToDashboard from '../components/BackToDashboard';
import { colors } from '../brand-colors';

function ExitStrategy() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromCommandCenter = searchParams.get('from') === 'cc';
  const [stage, setStage] = useState('education'); // education, selection, targets, generating, review, complete
  const [exitPath, setExitPath] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [analysisText, setAnalysisText] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true); // Prevents flash of wrong stage
  const [error, setError] = useState('');
  const [showHelperModal, setShowHelperModal] = useState(false);
  const [businessData, setBusinessData] = useState(null);
  const [copiedPrompt, setCopiedPrompt] = useState(false);
  const [debugPrompt, setDebugPrompt] = useState('');
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [monthlyPlan, setMonthlyPlan] = useState(null);
  const [plannedPromotions, setPlannedPromotions] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [isQuickStartUser, setIsQuickStartUser] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    checkAuthAndLoad();
    fetchPlannedPromotions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthAndLoad = async () => {
    // Check authentication
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Check if this is a Quick Start user (they skip Results & Alignment)
    const onboardingPathResult = await businessService.getField('onboarding_path');
    const onboardingPath = onboardingPathResult.success ? onboardingPathResult.data.field_value : null;
    const isQuickStart = onboardingPath === 'quick_start' || onboardingPath === 'quick_start_exploring';
    setIsQuickStartUser(isQuickStart);

    // Check if results & alignment is completed (skip for Quick Start users or users with products)
    if (!isQuickStart) {
      const alignmentResult = await businessService.getField('results_outcomes');
      if (!alignmentResult.success || !alignmentResult.data.field_value) {
        // Check if user has products (allow them to proceed)
        try {
          const productsRes = await fetch('/api/products/get-catalog', { credentials: 'include' });
          if (productsRes.ok) {
            const productsData = await productsRes.json();
            if (!productsData.catalog || productsData.catalog.length === 0) {
              setError('Please complete Results & Alignment first');
              setInitialLoading(false);
              return;
            }
            // User has products, allow them to proceed
          } else {
            setError('Please complete Results & Alignment first');
            setInitialLoading(false);
            return;
          }
        } catch (err) {
          setError('Please complete Results & Alignment first');
          setInitialLoading(false);
          return;
        }
      }
    }

    // Load existing exit strategy if any
    const result = await exitStrategyService.getAnalysis();
    console.log('[EXIT STRATEGY LOAD] getAnalysis result:', {
      success: result.success,
      hasExitPath: !!result.data?.exit_path,
      hasAnalysisText: !!result.data?.analysis_text,
      hasMonthlyPlan: !!result.data?.monthly_plan,
      exitPath: result.data?.exit_path,
      fullResult: result
    });

    if (result.success && result.data.exit_path && result.data.analysis_text) {
      setExitPath(result.data.exit_path);
      setAnalysisText(result.data.analysis_text);
      if (result.data.target_amount) setTargetAmount(result.data.target_amount);
      if (result.data.target_date) setTargetDate(result.data.target_date);
      if (result.data.monthly_plan) setMonthlyPlan(result.data.monthly_plan);

      // Check if user wants to edit - go to selection stage instead of complete
      const editMode = searchParams.get('edit') === 'true';
      if (editMode) {
        console.log('[EXIT STRATEGY LOAD] Edit mode - going to selection stage');
        setStage('selection');
      } else {
        console.log('[EXIT STRATEGY LOAD] Setting stage to complete');
        setStage('complete');
      }
    } else {
      console.log('[EXIT STRATEGY LOAD] NOT setting stage to complete - staying at education');
    }

    setInitialLoading(false);
  };

  const fetchPlannedPromotions = async () => {
    try {
      const response = await fetch('/api/planned-promotions/get-all', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPlannedPromotions(data.promotions || []);
      }
    } catch (err) {
      console.log('Error fetching promotions:', err);
    }
  };

  const handleExportPDF = async () => {
    if (!reportRef.current) return;

    setExporting(true);

    try {
      const element = reportRef.current;
      const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

      const opt = {
        margin: [0.5, 0.5, 0.5, 0.5],
        filename: `Exit-Strategy-${date.replace(/[,\s]+/g, '-')}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          letterRendering: true
        },
        jsPDF: {
          unit: 'in',
          format: 'letter',
          orientation: 'portrait'
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      await html2pdf().set(opt).from(element).save();
    } catch (err) {
      console.error('PDF export error:', err);
      setError('Failed to export PDF. Please try again.');
    }

    setExporting(false);
  };

  const handleContinueToTargets = () => {
    if (!exitPath) {
      setError('Please select an exit path');
      return;
    }

    if (exitPath === 'not_sure') {
      // User selected "Not Sure Yet" - skip back to appropriate location
      if (fromCommandCenter) {
        navigate('/command-center/exit-strategy');
      } else {
        navigate('/dashboard/foundation');
      }
      return;
    }

    // Move to targets stage
    setError('');
    setStage('targets');
  };

  const handleGenerate = async () => {
    console.log('handleGenerate called');
    console.log('exitPath:', exitPath);
    console.log('targetAmount:', targetAmount);
    console.log('targetDate:', targetDate);

    // Validate targets
    if (!targetAmount || !targetDate) {
      console.log('Validation failed - missing targetAmount or targetDate');
      setError('Please enter both your target amount and target date');
      return;
    }

    console.log('Validation passed, starting API call...');
    setLoading(true);
    setError('');
    setStage('generating');

    try {
      console.log('Calling exitStrategyService.generateAnalysis...');
      const result = await exitStrategyService.generateAnalysis(exitPath, targetAmount, targetDate);
      console.log('API call completed, result:', result);

      setLoading(false);

      if (result.success) {
        console.log('Success! Setting analysis data...');
        setAnalysisText(result.data.analysis_text);
        if (result.data.debug_prompt) {
          setDebugPrompt(result.data.debug_prompt);
        }
        if (result.data.monthly_plan) {
          setMonthlyPlan(result.data.monthly_plan);
        }
        setStage('review');
      } else {
        console.log('API returned error:', result.error);
        setError(result.error);
        setStage('targets');
      }
    } catch (err) {
      console.error('Unexpected error in handleGenerate:', err);
      setLoading(false);
      setError('An unexpected error occurred. Please try again.');
      setStage('targets');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');

    const result = await exitStrategyService.saveAnalysis(exitPath, analysisText, targetAmount, targetDate);

    setLoading(false);

    if (result.success) {
      // Command Center users go back to Command Center exit strategy page
      if (fromCommandCenter) {
        navigate('/command-center/exit-strategy');
      } else {
        // All users (including Quick Start) go to updated-exit-strategy to see their report
        // Quick Start users can then go to battle-plan from there
        navigate('/updated-exit-strategy');
      }
    } else {
      setError(result.error);
    }
  };

  const handleChangeSelection = () => {
    setStage('selection');
    setExitPath('');
    setTargetAmount('');
    setTargetDate('');
    setAnalysisText('');
  };

  const handleOpenHelper = async () => {
    // Fetch business data if not already loaded
    if (!businessData) {
      try {
        const fields = ['business_name', 'industry', 'business_stage', 'monthly_revenue', 'mission_statement', 'vision_statement'];
        const data = {};

        for (const field of fields) {
          const result = await businessService.getField(field);
          if (result.success) {
            data[field] = result.data.field_value || '';
          }
        }

        // Get core product from Product table
        try {
          const productsRes = await fetch('/api/products/get-catalog', { credentials: 'include' });
          if (productsRes.ok) {
            const productsData = await productsRes.json();
            const products = productsData.catalog || [];
            // Find core product (position='core' or fallback to non-tripwire)
            let coreProduct = products.find(p => p.position?.toLowerCase() === 'core');
            if (!coreProduct && products.length > 0) {
              coreProduct = products.find(p => !p.is_primary_tripwire && p.position?.toLowerCase() !== 'tripwire');
            }
            if (coreProduct) {
              data.core_product = coreProduct.name;
            }
          }
        } catch (e) {
          console.error('Error fetching products:', e);
        }

        setBusinessData(data);
      } catch (err) {
        console.error('Error fetching business data:', err);
      }
    }
    setShowHelperModal(true);
  };

  const handleCopyPrompt = () => {
    const promptText = generatePromptText();
    navigator.clipboard.writeText(promptText);
    setCopiedPrompt(true);
    setTimeout(() => setCopiedPrompt(false), 2000);
  };

  const generatePromptText = () => {
    if (!businessData) return '';

    const targetType = exitPath === 'lifestyle' ? 'annual profit target' : 'exit sale price';

    return `I want to determine realistic exit strategy targets for my business.

BUSINESS SITUATION (from my business plan):
- Business: ${businessData.business_name || '[Your Business]'} in ${businessData.industry || '[Industry]'}
- Current Stage: ${businessData.business_stage || '[Stage]'}
- Monthly Revenue: ${businessData.monthly_revenue || '[Revenue]'}
- Core Product/Service: ${businessData.core_product || '[Product]'}
- Mission: ${businessData.mission_statement || '[Mission]'}
- Vision: ${businessData.vision_statement || '[Vision]'}

NOW ADD YOUR PERSONAL INFORMATION:
[Replace the bracketed sections below with your details]

FAMILY & LIFESTYLE:
- Household: [number of people and their ages, e.g., "4 people - ages 45, 43, 16, 14"]
- Life Goals: [what you want to achieve - e.g., "send kids to college, travel to Europe annually, retire comfortably"]
- Timeline: [when you want this - e.g., "in 5 years" or "by 2030"]
- Location: [city, state - for cost of living context]

PERSONAL FINANCIAL SITUATION:
- Income outside business: [amount and source - e.g., "$50K/year from spouse's job - continuing"]
- Current household expenses: [monthly amount - e.g., "$8,000/month"]
- Expected changes: [e.g., "oldest starts college in 2 years, youngest in 6 years"]
- Current debt/obligations: [e.g., "$200K mortgage, $30K car loan"]
- Risk tolerance: [conservative / moderate / aggressive]

ADDITIONAL CONTEXT:
- Do you want to stay actively involved in the business or step away completely? [Answer here]
- Any non-negotiable constraints? [health issues, family obligations, etc.]
- What would "success" look like for you personally? [Describe your ideal outcome]

QUESTION:
Based on all of this information, what realistic ${targetType} and target date should I set for my "${getExitPathLabel(exitPath)}" exit strategy?

Please provide:
1. Recommended ${targetType} (specific dollar amount)
2. Recommended target date
3. Brief explanation of how you arrived at these numbers
4. Key assumptions I should validate`;
  };

  const getExitPathLabel = (path) => {
    const labels = {
      'lifestyle': 'Lifestyle Business (Keep Forever)',
      'build_to_sell': 'Build to Sell (3-7 Year Exit)',
      'acquisition': 'Scale for Acquisition (Venture Path)',
      'legacy': 'Legacy Business (Multi-Generational)',
      'not_sure': 'Not Sure Yet'
    };
    return labels[path] || path;
  };

  const renderEducation = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">Why Your Exit Strategy Matters Now</h2>

        <p className="mb-4">
          Your exit plan affects decisions you'll make <strong>THIS MONTH</strong>:
        </p>

        <Row className="mb-4">
          <Col md={6} className="mb-3">
            <Card className="h-100 border-primary">
              <Card.Body>
                <h5>💰 Pricing</h5>
                <ul className="small">
                  <li><strong>Lifestyle business:</strong> Price high, maximize profit per customer</li>
                  <li><strong>Build to sell:</strong> Price competitively, maximize customer count</li>
                  <li><strong>Acquisition target:</strong> Price for market share, worry about profit later</li>
                </ul>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6} className="mb-3">
            <Card className="h-100 border-primary">
              <Card.Body>
                <h5>📊 Business Model</h5>
                <ul className="small">
                  <li><strong>Lifestyle:</strong> One-time sales or simple subscriptions</li>
                  <li><strong>Build to sell:</strong> Recurring revenue (higher multiples)</li>
                  <li><strong>Acquisition:</strong> SaaS metrics, ARR, retention</li>
                </ul>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6} className="mb-3">
            <Card className="h-100 border-primary">
              <Card.Body>
                <h5>📈 Growth Strategy</h5>
                <ul className="small">
                  <li><strong>Lifestyle:</strong> Organic growth, word-of-mouth, sustainable</li>
                  <li><strong>Build to sell:</strong> Paid ads, fast growth, document everything</li>
                  <li><strong>Acquisition:</strong> Venture funding, blitz scaling, market dominance</li>
                </ul>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6} className="mb-3">
            <Card className="h-100 border-primary">
              <Card.Body>
                <h5>🏗️ Infrastructure</h5>
                <ul className="small">
                  <li><strong>Lifestyle:</strong> Minimal, you do most work</li>
                  <li><strong>Build to sell:</strong> Documented processes, can run without you</li>
                  <li><strong>Acquisition:</strong> Team-driven, systems, scalable tech stack</li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Alert variant="warning">
          There's no "right" answer - but there ARE wrong businesses for your goals.
          Let's figure out which path fits YOUR vision.
        </Alert>

        {error && <Alert variant="danger">{error}</Alert>}

        <div className="d-flex justify-content-between">
          <Button
            variant="outline-secondary"
            onClick={() => navigate(fromCommandCenter ? '/command-center/exit-strategy' : (isQuickStartUser ? '/quick-start-dashboard' : '/dashboard/foundation'))}
          >
            {fromCommandCenter ? '← Back to Exit Strategy' : 'Skip This (Come Back Later)'}
          </Button>
          <Button
            variant="primary"
            onClick={() => setStage('selection')}
          >
            Continue - Choose My Path
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  const renderSelection = () => (
    <Card className="shadow">
      <Card.Body className="p-4">
        <h2 className="mb-4">Choose Your Path</h2>

        <p className="text-muted mb-4">
          Select the exit strategy that aligns with your long-term vision:
        </p>

        {error && <Alert variant="danger">{error}</Alert>}

        <Form.Group className="mb-4">
          <Card className={`mb-3 ${exitPath === 'lifestyle' ? 'border-primary' : ''}`}>
            <Card.Body>
              <Form.Check
                type="radio"
                id="exit-lifestyle"
                name="exitPath"
                checked={exitPath === 'lifestyle'}
                onChange={() => setExitPath('lifestyle')}
                label={<strong>LIFESTYLE BUSINESS (Keep Forever)</strong>}
                className="mb-2"
              />
              <p className="small mb-2">
                <strong>Goal:</strong> Generate consistent income doing work you love<br />
                <strong>Timeline:</strong> Indefinite - you never "exit"<br />
                <strong>Target:</strong> $100K-500K annual profit, sustainable workload
              </p>
              <p className="small mb-0 text-muted">
                <strong>Best for:</strong> You love the work and want to do it long-term. Work-life balance
                matters more than maximum growth. You want control and simplicity.
              </p>
            </Card.Body>
          </Card>

          <Card className={`mb-3 ${exitPath === 'build_to_sell' ? 'border-primary' : ''}`}>
            <Card.Body>
              <Form.Check
                type="radio"
                id="exit-build-to-sell"
                name="exitPath"
                checked={exitPath === 'build_to_sell'}
                onChange={() => setExitPath('build_to_sell')}
                label={<strong>BUILD TO SELL (3-7 Year Exit)</strong>}
                className="mb-2"
              />
              <p className="small mb-2">
                <strong>Goal:</strong> Build valuable business, sell it, move to next venture<br />
                <strong>Timeline:</strong> 3-7 years to exit<br />
                <strong>Target:</strong> $1M-10M sale price (typically 3-5x revenue or 10-15x profit)
              </p>
              <p className="small mb-0 text-muted">
                <strong>Best for:</strong> Serial entrepreneur, empire builder. You enjoy building but not
                running long-term. You want a liquidity event (cash out).
              </p>
            </Card.Body>
          </Card>

          <Card className={`mb-3 ${exitPath === 'acquisition' ? 'border-primary' : ''}`}>
            <Card.Body>
              <Form.Check
                type="radio"
                id="exit-acquisition"
                name="exitPath"
                checked={exitPath === 'acquisition'}
                onChange={() => setExitPath('acquisition')}
                label={<strong>SCALE FOR ACQUISITION (Venture Path)</strong>}
                className="mb-2"
              />
              <p className="small mb-2">
                <strong>Goal:</strong> Grow fast, get acquired by larger company<br />
                <strong>Timeline:</strong> 5-10 years, likely with outside funding<br />
                <strong>Target:</strong> $10M-100M+ exit (or IPO)
              </p>
              <p className="small mb-0 text-muted">
                <strong>Best for:</strong> You want to "go big". You're okay with investors/board oversight.
                You thrive in high-growth, high-pressure environments.
              </p>
            </Card.Body>
          </Card>

          <Card className={`mb-3 ${exitPath === 'legacy' ? 'border-primary' : ''}`}>
            <Card.Body>
              <Form.Check
                type="radio"
                id="exit-legacy"
                name="exitPath"
                checked={exitPath === 'legacy'}
                onChange={() => setExitPath('legacy')}
                label={<strong>LEGACY BUSINESS (Multi-Generational)</strong>}
                className="mb-2"
              />
              <p className="small mb-2">
                <strong>Goal:</strong> Build something that lasts for decades<br />
                <strong>Timeline:</strong> 20+ years, possibly passed to family/team<br />
                <strong>Target:</strong> Sustained profitability, strong reputation
              </p>
              <p className="small mb-0 text-muted">
                <strong>Best for:</strong> Building for impact and longevity. You care about legacy more
                than exit. Family business or mission-driven.
              </p>
            </Card.Body>
          </Card>

          <Card className={`mb-3 ${exitPath === 'not_sure' ? 'border-secondary' : ''}`}>
            <Card.Body>
              <Form.Check
                type="radio"
                id="exit-not-sure"
                name="exitPath"
                checked={exitPath === 'not_sure'}
                onChange={() => setExitPath('not_sure')}
                label={<strong>NOT SURE YET (Figure It Out Later)</strong>}
                className="mb-2"
              />
              <p className="small mb-0 text-muted">
                You can skip this and come back when you have more clarity.
              </p>
            </Card.Body>
          </Card>
        </Form.Group>

        <div className="d-flex justify-content-between">
          <Button
            variant="outline-secondary"
            onClick={() => setStage('education')}
          >
            Back
          </Button>
          <Button
            variant="primary"
            onClick={handleContinueToTargets}
            disabled={!exitPath}
          >
            {exitPath === 'not_sure' ? 'Skip for Now' : 'Continue →'}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  const renderDebugModal = () => (
    <Modal show={showDebugModal} onHide={() => setShowDebugModal(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>AI Prompt Debug View</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info">
          <strong>Alpha Test Mode:</strong> This shows exactly what was sent to the AI to generate your exit strategy analysis.
        </Alert>
        <Form.Control
          as="textarea"
          rows={20}
          value={debugPrompt}
          readOnly
          style={{
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            whiteSpace: 'pre-wrap'
          }}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowDebugModal(false)}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const renderHelperModal = () => (
    <Modal show={showHelperModal} onHide={() => setShowHelperModal(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>AI Target Calculator</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info">
          <strong>How this works:</strong> Copy the prompt below and paste it into ChatGPT, Claude, Grok, or your favorite AI.
          We've pre-filled your business information - you just add your personal details.
          After your AI conversation, come back and enter the recommended targets.
          <hr className="my-2" />
          <small className="text-muted">
            <strong>Privacy first:</strong> Your personal financial and family information stays between you and your AI -
            we never see or store it on our servers.
          </small>
        </Alert>

        <div className="mb-3">
          <strong>Quick Links (open in new tab):</strong>
          <div className="mt-2">
            <Button
              variant="outline-primary"
              size="sm"
              className="me-2"
              onClick={() => window.open('https://chat.openai.com', '_blank')}
            >
              Open ChatGPT
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              className="me-2"
              onClick={() => window.open('https://claude.ai', '_blank')}
            >
              Open Claude
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => window.open('https://grok.x.com', '_blank')}
            >
              Open Grok
            </Button>
          </div>
        </div>

        <Form.Group className="mb-3">
          <Form.Label><strong>Copy This Prompt:</strong></Form.Label>
          <Form.Control
            as="textarea"
            rows={15}
            value={generatePromptText()}
            readOnly
            style={{
              fontFamily: 'monospace',
              fontSize: '0.85rem'
            }}
          />
        </Form.Group>

        <Button
          variant="primary"
          onClick={handleCopyPrompt}
          className="w-100"
        >
          {copiedPrompt ? '✓ Copied!' : '📋 Copy Prompt to Clipboard'}
        </Button>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowHelperModal(false)}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const renderTargets = () => {
    const isLifestyle = exitPath === 'lifestyle';
    const amountLabel = isLifestyle ? 'Target Annual Profit' : 'Exit Sale Price';
    const dateLabel = isLifestyle ? 'Target Date to Achieve' : 'Exit Date';
    const amountPlaceholder = isLifestyle ? 'e.g., $500,000' : 'e.g., $10,000,000';

    return (
      <Card className="shadow">
        <Card.Body className="p-4">
          <h2 className="mb-4">Set Your Specific Targets</h2>

          <Alert variant="info" className="mb-4">
            <strong>Why specific targets matter:</strong> These concrete numbers allow us to track your progress
            in the Profit Center and alert you if you're off track. We'll compare your actual sales trajectory
            with what's required to hit your goal.
          </Alert>

          <div className="text-center mb-4">
            <Button
              variant="outline-info"
              onClick={handleOpenHelper}
              size="lg"
            >
              🤔 Need Help Figuring Out Your Targets? Get AI Guidance
            </Button>
          </div>

          <p className="text-muted mb-4">
            <strong>Your Path:</strong> {getExitPathLabel(exitPath)}
          </p>

          {error && <Alert variant="danger">{error}</Alert>}

          <Form>
            <Form.Group className="mb-4">
              <Form.Label><strong>{amountLabel}</strong></Form.Label>
              <Form.Control
                type="text"
                placeholder={amountPlaceholder}
                value={targetAmount}
                onChange={(e) => setTargetAmount(e.target.value)}
              />
              <Form.Text className="text-muted">
                {isLifestyle
                  ? 'How much do you want to be earning annually? (e.g., $500,000 to pay for kids\' college and travel)'
                  : 'How much do you want to sell your business for? (e.g., $10,000,000)'}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label><strong>{dateLabel}</strong></Form.Label>
              <Form.Control
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
              <Form.Text className="text-muted">
                {isLifestyle
                  ? 'When do you want to reach this annual profit level?'
                  : 'When do you want to exit/sell your business?'}
              </Form.Text>
            </Form.Group>
          </Form>

          <div className="d-flex justify-content-between mt-4">
            <Button
              variant="outline-secondary"
              onClick={() => setStage('selection')}
            >
              ← Back
            </Button>
            <Button
              variant="primary"
              onClick={handleGenerate}
              disabled={!targetAmount || !targetDate || loading}
            >
              {loading ? 'Analyzing...' : 'Generate Strategy →'}
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  };

  const renderGenerating = () => (
    <Card className="shadow">
      <Card.Body className="p-5 text-center">
        <Spinner animation="border" variant="primary" className="mb-3" />
        <h4>Analyzing your {getExitPathLabel(exitPath)}...</h4>
        <p className="text-muted">
          This analysis may take 60-90 seconds.
        </p>
      </Card.Body>
    </Card>
  );

  const renderReview = () => (
    <>
      {/* Export Button - Outside the ref so it doesn't appear in PDF */}
      <div className="d-flex justify-content-end mb-3">
        <Button
          variant="outline-primary"
          onClick={handleExportPDF}
          disabled={exporting}
        >
          {exporting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Exporting...
            </>
          ) : (
            '📄 Export to PDF'
          )}
        </Button>
      </div>

      <div ref={reportRef}>
        <Card className="shadow">
          <Card.Body className="p-4">
            <div className="mb-4">
              <h2 className="mb-1">Your Exit Strategy</h2>
              <h4 style={{ color: colors.greyDark, fontWeight: 500 }}>{getExitPathLabel(exitPath)}</h4>
              {targetAmount && (
                <h5 style={{ color: colors.goldDark, fontWeight: 600 }}>
                  Goal: ${Number(targetAmount).toLocaleString()}
                </h5>
              )}
            </div>

            <p className="text-muted mb-4">
              Review how this exit strategy should influence your immediate business decisions.
            </p>

            {error && <Alert variant="danger">{error}</Alert>}

        {/* 30-Day Acquisition Plan */}
        {monthlyPlan && monthlyPlan.length > 0 && monthlyPlan[0] && (
          <Card className="mb-4 border-primary">
            <Card.Body>
              <h5 className="mb-3">📊 Your First 30 Days</h5>
              <p className="text-muted small mb-3">
                Here's what needs to happen in Month 1 to stay on track for your {targetDate} goal:
              </p>

              {/* Scheduled Promotions */}
              {monthlyPlan[0].promo_notes && monthlyPlan[0].promo_notes.length > 0 && (
                <div className="mb-3">
                  <strong>🎯 Scheduled Promotions:</strong>
                  <ul className="mb-0 mt-2">
                    {monthlyPlan[0].promo_notes.map((note, index) => (
                      <li key={index}>{note}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Organic Acquisitions */}
              {monthlyPlan[0].entry_acquisitions && Object.keys(monthlyPlan[0].entry_acquisitions).length > 0 && (
                <div className="mb-3">
                  <strong>🌱 New Organic Customer Acquisitions:</strong>
                  <ul className="mb-0 mt-2">
                    {Object.entries(monthlyPlan[0].entry_acquisitions).map(([productName, count], index) => (
                      <li key={index}>
                        <strong>{count}</strong> new customers for <strong>{productName}</strong>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Expected Upgrades */}
              {monthlyPlan[0].upgrade_notes && monthlyPlan[0].upgrade_notes.length > 0 && (
                <div className="mb-0">
                  <strong>⬆️ Expected Upgrades:</strong>
                  <ul className="mb-0 mt-2 text-muted small">
                    {monthlyPlan[0].upgrade_notes.map((note, index) => (
                      <li key={index}>{note}</li>
                    ))}
                  </ul>
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Revenue Milestones */}
        {monthlyPlan && monthlyPlan.length > 1 && (
          <Card className="mb-4 border-success">
            <Card.Body>
              <h5 className="mb-3">📈 Revenue Milestones</h5>
              <p className="text-muted small mb-3">
                {monthlyPlan.some(m => m.recurring_customers > 0)
                  ? 'Projected MRR at key milestones (accounting for churn and ramp-up):'
                  : 'Projected revenue milestones for your one-time products:'}
              </p>

              <div className="table-responsive">
                <table className="table table-sm">
                  {monthlyPlan.some(m => m.recurring_customers > 0) ? (
                    /* Table for businesses WITH recurring products */
                    <>
                      <thead>
                        <tr>
                          <th>Month</th>
                          <th>
                            MRR
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>Monthly Recurring Revenue - The predictable revenue you receive each month from subscriptions and recurring purchases.</Tooltip>}
                            >
                              <span className="ms-1 text-muted" style={{ cursor: 'help' }}>ℹ️</span>
                            </OverlayTrigger>
                          </th>
                          <th>
                            ARR
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>Annual Recurring Revenue - Your MRR multiplied by 12. This is the annualized value of your recurring revenue and a key metric for business valuation.</Tooltip>}
                            >
                              <span className="ms-1 text-muted" style={{ cursor: 'help' }}>ℹ️</span>
                            </OverlayTrigger>
                          </th>
                          <th style={{ whiteSpace: 'normal', minWidth: '80px' }}>Annual Profit</th>
                          <th style={{ whiteSpace: 'normal', minWidth: '80px' }}>Recurring Customers</th>
                          {monthlyPlan.some(m => m.lifetime_customers > 0) && <th style={{ whiteSpace: 'normal', minWidth: '80px' }}>Lifetime Customers</th>}
                          {monthlyPlan.some(m => m.churn_loss > 0) && (
                            <th style={{ whiteSpace: 'normal', minWidth: '80px' }}>
                              Monthly Churn
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>Monthly Churn Loss - The revenue lost each month from customers who cancel their subscriptions.</Tooltip>}
                              >
                                <span className="ms-1 text-muted" style={{ cursor: 'help' }}>ℹ️</span>
                              </OverlayTrigger>
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {monthlyPlan.filter(m => m.month > 1).map((milestone, index) => (
                          <tr key={index}>
                            <td><strong>Month {milestone.month}</strong></td>
                            <td>${milestone.current_mrr?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                            <td>${((milestone.current_mrr || 0) * 12).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td className="text-success">${milestone.annual_profit?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) || '0'}</td>
                            <td>{milestone.recurring_customers || 0}</td>
                            {monthlyPlan.some(m => m.lifetime_customers > 0) && (
                              <td className="text-muted">{milestone.lifetime_customers || 0}</td>
                            )}
                            {monthlyPlan.some(m => m.churn_loss > 0) && (
                              <td className="text-danger">
                                ${milestone.churn_loss?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </>
                  ) : (
                    /* Table for businesses with ONLY one-time products */
                    <>
                      <thead>
                        <tr>
                          <th>Month</th>
                          <th>Monthly Sales</th>
                          <th>
                            Cumulative Revenue
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>Total revenue earned from all one-time sales up to this month.</Tooltip>}
                            >
                              <span className="ms-1 text-muted" style={{ cursor: 'help' }}>ℹ️</span>
                            </OverlayTrigger>
                          </th>
                          <th style={{ whiteSpace: 'normal', minWidth: '80px' }}>Total Customers</th>
                        </tr>
                      </thead>
                      <tbody>
                        {monthlyPlan.filter(m => m.month > 1).map((milestone, index) => (
                          <tr key={index}>
                            <td><strong>Month {milestone.month}</strong></td>
                            <td>${milestone.one_time_revenue_this_month?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                            <td className="text-success">${milestone.cumulative_one_time_revenue?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                            <td>{milestone.total_customers || milestone.one_time_customers || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </>
                  )}
                </table>
              </div>

              <p className="text-muted small mb-0 mt-2">
                {monthlyPlan.some(m => m.recurring_customers > 0) ? (
                  <>
                    <strong>Note:</strong> Annual Profit is calculated as ARR multiplied by your average profit margin.
                    {monthlyPlan.some(m => m.churn_loss > 0) && ' Projections account for monthly customer churn based on your product churn rates.'}
                    {monthlyPlan.some(m => m.lifetime_customers > 0) && ' Lifetime deal customers (e.g., AppSumo) are shown separately - they don\'t generate recurring MRR until they upgrade.'}
                  </>
                ) : (
                  <><strong>Note:</strong> For one-time products, revenue is based on projected monthly sales. Cumulative revenue shows your total earnings over time.</>
                )}
              </p>

              <div className="alert alert-info small mt-3 mb-0">
                <strong>Important:</strong> These projections will be adjusted quarterly to ensure you hit your exit target.
                For a successful acquisition, buyers typically want to see <strong>12+ months of sustained revenue</strong> at target levels before closing.
                Plan to reach your target MRR by Month {Math.max(1, (monthlyPlan[0]?._projection_metadata?.exit_month || 60) - 12)} to allow time for demonstrated stability.
              </div>

              {/* Overshoot Warning - when promos exceed target significantly */}
              {monthlyPlan[0]?._projection_metadata?.overshoot_ratio > 1.5 && (
                <div className="alert alert-warning small mt-3 mb-0">
                  <strong>Your promotions are projected to exceed your target!</strong>
                  <p className="mb-2 mt-2">
                    Based on your planned promotions and upgrade paths, your projected exit MRR of{' '}
                    <strong>${(monthlyPlan[0]._projection_metadata.exit_final_mrr || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</strong>{' '}
                    is <strong>{((monthlyPlan[0]._projection_metadata.overshoot_ratio - 1) * 100).toFixed(0)}% higher</strong> than your target of{' '}
                    <strong>${(monthlyPlan[0]._projection_metadata.target_mrr_at_exit || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</strong>.
                  </p>
                  <p className="mb-2">
                    <strong>The upside:</strong> If you hit these numbers, your business could be valued at approximately{' '}
                    <strong>${((monthlyPlan[0]._projection_metadata.exit_final_mrr || 0) * 12 * 3).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</strong>{' '}
                    (at 3x ARR) instead of your original {targetAmount} target.
                  </p>
                  <p className="mb-0">
                    <strong>The requirement:</strong> To sustain these numbers, you'll need to maintain approximately{' '}
                    <strong>{Math.round(monthlyPlan[0]._projection_metadata.monthly_organic_growth || 0)}</strong> new organic customers per month
                    through your entire timeline. If this feels aggressive, consider reducing your promotion targets in{' '}
                    <a href="/planned-promotions">Planned Promotions</a> to a more sustainable growth rate.
                  </p>
                </div>
              )}

              <div className="mt-3 text-center">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={async () => {
                    try {
                      const response = await fetch('/api/exit-strategy/download-csv', {
                        credentials: 'include'
                      });
                      if (response.ok) {
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `exit_strategy_breakdown_${new Date().toISOString().slice(0,10)}.csv`;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        a.remove();
                      } else {
                        const errorData = await response.json();
                        alert(errorData.error || 'Error downloading CSV');
                      }
                    } catch (err) {
                      alert('Error downloading CSV: ' + err.message);
                    }
                  }}
                >
                  📥 Download Month-by-Month CSV (Debug)
                </Button>
              </div>

              {/* Planned Promotions Section */}
              {plannedPromotions.length > 0 && (
                <div className="mt-4 pt-3 border-top">
                  <h6 className="mb-3">Planned Promotions Impact</h6>
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Promotion</th>
                          <th>Product</th>
                          <th>Date Range</th>
                          <th>Sale Price</th>
                          <th style={{ whiteSpace: 'normal', minWidth: '70px' }}>Est. Sales</th>
                          <th style={{ whiteSpace: 'normal', minWidth: '80px' }}>Est. Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {plannedPromotions.map((promo, index) => (
                          <tr key={index}>
                            <td><strong>{promo.promotion_name}</strong></td>
                            <td>{promo.product_name}</td>
                            <td>
                              {new Date(promo.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(promo.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </td>
                            <td className="text-success">${promo.sale_price.toFixed(2)}</td>
                            <td>{promo.estimated_sales}</td>
                            <td className="text-success">${(promo.sale_price * promo.estimated_sales).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="table-success">
                          <td colSpan="4"></td>
                          <td><strong>Total:</strong></td>
                          <td>
                            <strong className="text-success">
                              ${plannedPromotions.reduce((sum, p) => sum + (p.sale_price * p.estimated_sales), 0).toLocaleString()}
                            </strong>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <p className="text-muted small mb-0">
                    <strong>Note:</strong> These planned promotions represent additional revenue opportunities.{' '}
                    <a href="/planned-promotions">Manage promotions</a>
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        <Card className="mb-4 border-info">
          <Card.Body>
            <h5 className="mb-3">Analysis Sections:</h5>
            <ul>
              <li><strong>Section 1:</strong> Pricing Strategy</li>
              <li><strong>Section 2:</strong> Business Model</li>
              <li><strong>Section 3:</strong> Infrastructure Decisions</li>
              <li><strong>Section 4:</strong> Growth Strategy</li>
              <li><strong>Section 5:</strong> Red Flags to Avoid</li>
              <li><strong>Section 6:</strong> Milestones & Timeline</li>
            </ul>
          </Card.Body>
        </Card>

        <div className="mb-4">
          <div style={{
            fontFamily: 'system-ui, -apple-system, sans-serif',
            fontSize: '0.95em',
            lineHeight: '1.6',
            backgroundColor: '#f8f9fa',
            padding: '20px',
            borderRadius: '5px',
            border: '1px solid #dee2e6'
          }}>
            <ReactMarkdown>{analysisText}</ReactMarkdown>
          </div>
        </div>

          </Card.Body>
        </Card>
      </div>

      {/* Action buttons - Outside ref so they don't appear in PDF */}
      {debugPrompt && (
        <div className="mb-3 text-center mt-3">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowDebugModal(true)}
          >
            🔍 View AI Prompt (Debug)
          </Button>
        </div>
      )}

      {/* Action buttons - hide "Save & Continue" when viewing from Command Center */}
      <div className="d-flex justify-content-between mt-3">
        {fromCommandCenter ? (
          <Button
            variant="outline-secondary"
            onClick={() => navigate('/command-center/exit-strategy')}
          >
            ← Back to Exit Strategy
          </Button>
        ) : (
          <>
            <Button
              variant="outline-secondary"
              onClick={handleChangeSelection}
              disabled={loading}
            >
              Change Exit Path
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save & Continue'}
            </Button>
          </>
        )}
      </div>
    </>
  );

  // renderComplete shows the same full content as renderReview
  // This ensures consistent display whether viewing after generation or loading saved data
  const renderComplete = () => renderReview();

  // Show loading spinner while determining which stage to display
  if (initialLoading) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={10} lg={8} className="text-center">
            <Spinner animation="border" role="status" className="mb-3" />
            <p className="text-muted">Loading your exit strategy...</p>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <>
      {renderDebugModal()}
      {renderHelperModal()}
      <Container className="py-5">
        <BackToDashboard module="foundation" />
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            {stage === 'education' && renderEducation()}
            {stage === 'selection' && renderSelection()}
            {stage === 'targets' && renderTargets()}
            {stage === 'generating' && renderGenerating()}
            {stage === 'review' && renderReview()}
            {stage === 'complete' && renderComplete()}
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default ExitStrategy;
